import { TILE_SIZES, type TileSize } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { TWO_TOWER_MODEL_TRIGGER } from 'dibs-personalized/exports/UserRecommendationsGrid.ts';
import { triggerConstants } from 'dibs-tracking';
import {
    DESKTOP_ITEMS_PER_ROW,
    MOBILE_ITEMS_PER_ROW,
} from '../homepage/HpSharedItemsGrid/constants.ts';
import { type HomepageMixedGridItemSource } from './__generated__/HpPersonalizedMixedGridResp_viewer.graphql.ts';

const { TRIGGER_EDITORS_PICKS } = triggerConstants;

export const MIXED_GRID_FIRST = 20;
export const MIXED_GRID_SLOT_COUNT = 24;
export const MIXED_GRID_FETCH_COUNT = MIXED_GRID_SLOT_COUNT + 1;
export const MIXED_GRID_DESKTOP_INITIAL_FETCH_COUNT = 8;
export const MIXED_GRID_MOBILE_INITIAL_FETCH_COUNT = 9;
export const MIXED_GRID_EDITORIAL_COLLECTION_COUNT = 4;
export const MIXED_GRID_MOBILE_SLOT_COUNT =
    MIXED_GRID_SLOT_COUNT - MIXED_GRID_EDITORIAL_COLLECTION_COUNT;
export const MIXED_GRID_MOBILE_CAROUSEL_INDEX = 8;

export function getHpPersonalizedMixedGridInitialFetchCount(isMobile: boolean): number {
    return isMobile
        ? MIXED_GRID_MOBILE_INITIAL_FETCH_COUNT
        : MIXED_GRID_DESKTOP_INITIAL_FETCH_COUNT;
}

export function getHpPersonalizedMixedGridFetchCount(isMobile: boolean): number {
    // Mobile still receives editorial collection tiles in `items`; they are rendered
    // in the carousel, not as grid slots, so fetch enough extra listing items to fill them.
    return isMobile
        ? MIXED_GRID_MOBILE_SLOT_COUNT + MIXED_GRID_EDITORIAL_COLLECTION_COUNT
        : MIXED_GRID_FETCH_COUNT;
}

export const MIXED_GRID_DESKTOP_ITEMS_PER_ROW = DESKTOP_ITEMS_PER_ROW;

type MixedGridVisibleEdge =
    | {
          readonly node?: {
              readonly item?: {
                  readonly __typename: string;
              } | null;
          } | null;
      }
    | null
    | undefined;

function getLastListingItemIndex<T extends MixedGridVisibleEdge>(edges: readonly T[]): number {
    for (let index = edges.length - 1; index >= 0; index -= 1) {
        if (edges[index]?.node?.item?.__typename === 'Item') {
            return index;
        }
    }

    return -1;
}

export function getHpPersonalizedMixedGridVisibleEdges<T extends MixedGridVisibleEdge>(
    edges: readonly T[],
    includeAd: boolean
): T[] {
    if (includeAd) {
        return edges.slice(0, MIXED_GRID_SLOT_COUNT);
    }

    const adIndex = edges.findIndex(
        edge => edge?.node?.item?.__typename === 'HomepageMixedGridAdSlot'
    );
    if (adIndex === -1) {
        return edges.slice(0, MIXED_GRID_SLOT_COUNT);
    }

    if (edges.length <= MIXED_GRID_SLOT_COUNT) {
        return edges
            .filter(edge => edge?.node?.item?.__typename !== 'HomepageMixedGridAdSlot')
            .slice(0, MIXED_GRID_SLOT_COUNT);
    }

    const fallbackItemIndex = getLastListingItemIndex(edges);
    const fallbackItem = fallbackItemIndex === -1 ? undefined : edges[fallbackItemIndex];
    if (fallbackItemIndex === -1 || fallbackItem === undefined) {
        return edges
            .filter(edge => edge?.node?.item?.__typename !== 'HomepageMixedGridAdSlot')
            .slice(0, MIXED_GRID_SLOT_COUNT);
    }

    const visibleEdges = edges.filter((_, index) => index !== fallbackItemIndex);
    const fallbackAdIndex = adIndex > fallbackItemIndex ? adIndex - 1 : adIndex;
    // swap failed to load ad with the last listing item
    visibleEdges[fallbackAdIndex] = fallbackItem;

    return visibleEdges.slice(0, MIXED_GRID_SLOT_COUNT);
}

export function getHpPersonalizedMixedGridShimmerCount(
    visibleCount: number,
    isMobile = false
): number {
    const slotCount = isMobile ? MIXED_GRID_MOBILE_SLOT_COUNT : MIXED_GRID_SLOT_COUNT;
    const remaining = Math.max(slotCount - visibleCount, 0);
    if (!isMobile) {
        return remaining;
    }

    const leftover = (visibleCount + remaining) % MOBILE_ITEMS_PER_ROW;
    return leftover === 0 ? remaining : Math.max(remaining - leftover, 0);
}

export function getHpPersonalizedMixedGridListingItemTrackingTrigger(
    itemSource: HomepageMixedGridItemSource | null | undefined
): string {
    switch (itemSource) {
        case 'RECOMMENDATIONS':
            return TWO_TOWER_MODEL_TRIGGER;
        case 'EDITORS_PICKS':
            return TRIGGER_EDITORS_PICKS;
        default:
            return TRIGGER_EDITORS_PICKS;
    }
}

export function getHpPersonalizedMixedGridProductTileSize(index: number): TileSize {
    const row = Math.floor(index / MIXED_GRID_DESKTOP_ITEMS_PER_ROW);

    if (row === 0) {
        return TILE_SIZES.xxxLarge;
    }

    if (row === 5) {
        return TILE_SIZES.xLarge;
    }

    return (row + (index % MIXED_GRID_DESKTOP_ITEMS_PER_ROW)) % 2 === 0
        ? TILE_SIZES.xLarge
        : TILE_SIZES.xxLarge;
}
